/**
 * Blog Page - 90 Days of Content
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Clock, ArrowRight, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { blogPosts, categories } from "../data/blogPosts";

const BLOG_HERO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663400393649/gEFtNmSeGBDwQH79ZrNHXT/blog_hero-L4xqQAnK1cGbKxc6bsKE8Q.webp";

const POSTS_PER_PAGE = 12;

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = useMemo(() => {
    return filtered.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [filtered, startIndex]);

  const featuredPost = blogPosts.find((p) => p.featured);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden pt-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BLOG_HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52]/95 via-[#0f2d52]/80 to-[#0f2d52]/30" />
        <div className="container relative z-10 py-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Blog</span>
            </div>
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl md:text-5xl font-black text-white mb-4">
              Insights & <span className="text-[#48d480]">Resources</span>
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 text-lg">
              Expert advice on business funding, credit building, and financial growth.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-12 bg-gray-50">
          <div className="container">
            <Link to={`/blog/${featuredPost.slug}`} className="block group">
              <div className="grid lg:grid-cols-2 gap-8 bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                <div className="h-64 lg:h-auto overflow-hidden">
                  <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="inline-block bg-[#48d480] text-[#0f2d52] text-xs font-bold px-3 py-1 rounded-full mb-4 w-fit">Featured</span>
                  <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl md:text-3xl font-black text-[#0f2d52] mb-3 group-hover:text-[#48d480] transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 mb-4">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>{featuredPost.category}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><Clock size={14} /> {featuredPost.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? "bg-[#0f2d52] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <p className="text-gray-500">
              Showing {startIndex + 1}-{Math.min(startIndex + POSTS_PER_PAGE, filtered.length)} of {filtered.length} articles
            </p>
          </div>
          
          {paginatedPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedPosts.filter((p) => !p.featured || currentPage > 1).map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
                  <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all h-full flex flex-col">
                    <div className="h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <span className="text-[#48d480] text-xs font-bold mb-2">{post.category}</span>
                      <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-lg mb-2 group-hover:text-[#48d480] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm flex-1 mb-4 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{post.date}</span>
                        <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No articles found matching your criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={20} />
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first, last, current, and adjacent pages
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg font-medium transition-all ${
                        currentPage === page
                          ? "bg-[#0f2d52] text-white"
                          : "border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return <span key={page} className="px-2">...</span>;
                }
                return null;
              })}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl font-black text-white mb-4">
            Ready to Get <span className="text-[#48d480]">Started?</span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-8">
            Apply now and let us help you find the perfect funding solution.
          </p>
          <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer">
            <button className="flex items-center gap-2 mx-auto bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-6 py-3 rounded-lg transition-colors">
              Apply Now <ArrowRight size={16} />
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
